import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { BookOpen, Search, Building2, Loader2 } from "lucide-react";

const CATS = ["all", "air", "water", "spill", "waste", "asbestos", "noise", "heat", "chemical"];

export default function KnowledgeBase() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/knowledge").then((r) => { setItems(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const filtered = items.filter((i) => {
    const okCat = cat === "all" || i.category === cat;
    const okQ = !q || (i.title + i.summary + i.citation).toLowerCase().includes(q.toLowerCase());
    return okCat && okQ;
  });

  return (
    <div data-testid="knowledge-page" className="space-y-6">
      <div className="relative overflow-hidden rounded-sm border border-[#DCE3DD]">
        <img src="https://images.unsplash.com/photo-1779275396996-c65c4ec22823?crop=entropy&cs=srgb&fm=jpg&q=85" alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#275C3F]/85" />
        <div className="relative p-8 text-white">
          <h1 className="font-display text-3xl font-bold flex items-center gap-2"><BookOpen className="h-7 w-7" /> {t("kb.title")}</h1>
          <p className="mt-2 text-white/85 max-w-xl">{t("kb.subtitle")}</p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#4B5950]" />
          <input data-testid="knowledge-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("kb.searchPlaceholder")} className="w-full bg-white border border-[#DCE3DD] rounded-sm pl-9 pr-3 py-2.5 text-sm focus:ring-2 focus:ring-[#275C3F] focus:ring-offset-1 outline-none" />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {CATS.map((c) => (
            <button key={c} data-testid={`kb-filter-${c}`} onClick={() => setCat(c)} className={`text-xs font-medium px-3 py-1.5 rounded-sm border transition-all duration-200 ${cat === c ? "bg-[#275C3F] text-white border-[#275C3F]" : "bg-white border-[#DCE3DD] text-[#4B5950] hover:bg-[#EAF0EA]"}`}>
              {t(`kbcat.${c}`)}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#275C3F]" /></div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((i) => (
            <div key={i.id} data-testid={`kb-card-${i.id}`} className="bg-white border border-[#DCE3DD] rounded-sm p-5 hover:border-[#275C3F]/40 transition-all duration-200">
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-0.5 rounded-sm bg-[#EAF0EA] text-[#275C3F]"><Building2 className="h-3 w-3" /> {i.agency}</span>
                <span className="text-xs text-[#2E7599] font-medium">{i.citation}</span>
              </div>
              <h3 className="font-display text-lg font-semibold mt-3">{i.title}</h3>
              <p className="text-sm text-[#4B5950] mt-2 leading-relaxed">{i.summary}</p>
            </div>
          ))}
          {filtered.length === 0 && <p className="text-[#4B5950] col-span-full text-center py-6">{t("kb.noMatch")}</p>}
        </div>
      )}
    </div>
  );
}
